/**
 * Resolve API base URL for browser vs build-time config.
 *
 * In local Docker dev, VITE_API_URL is sometimes set to http://backend:8000/api/v1
 * which only works inside the compose network — the browser must use same-origin
 * /api/v1 (Vite/nginx proxy) or http://localhost:8000/api/v1.
 */
export function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    // Dev server always proxies /api/v1 — never call Docker service names from the browser.
    if (import.meta.env.DEV) {
      return '/api/v1';
    }

    const { hostname, protocol } = window.location;
    if (
      protocol === 'http:' &&
      (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0')
    ) {
      return '/api/v1';
    }
  }

  const configured = import.meta.env.VITE_API_URL;

  if (!configured || configured === '/api/v1') {
    return '/api/v1';
  }

  if (typeof window === 'undefined') {
    return configured;
  }

  // Relative path — use Vite/nginx proxy.
  if (configured.startsWith('/')) {
    return configured;
  }

  try {
    const parsed = new URL(configured);
    const isDockerInternal =
      parsed.hostname === 'backend' ||
      parsed.hostname === 'frontend' ||
      parsed.hostname.endsWith('.internal');

    if (isDockerInternal) {
      return '/api/v1';
    }

    // Absolute localhost URL from browser on another port — allowed (CORS on backend).
    return configured;
  } catch {
    return '/api/v1';
  }
}
